import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Check, Trash2, CalendarClock, AlertTriangle } from "lucide-react";
import { api, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export const RECORD_TYPES = [
  { v: "supervision", l: "Supervision" },
  { v: "annual_review", l: "Annual Review" },
  { v: "kpi_review", l: "KPI Review" },
  { v: "annual_assessment", l: "Annual Assessment" },
  { v: "field_visit", l: "6-Monthly Field Visit" },
  { v: "handwashing", l: "Handwashing Audit" },
  { v: "reflection", l: "Reflection" },
  { v: "development_plan", l: "Development Plan" },
];
const LABEL = Object.fromEntries(RECORD_TYPES.map((r) => [r.v, r.l]));
const ALL = "__all__";

export default function Reviews() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [typeF, setTypeF] = useState(ALL);
  const [statusF, setStatusF] = useState(ALL);
  const [open, setOpen] = useState(false);

  const load = () => {
    const params = {};
    if (user.role === "rcsm" || user.role === "cnat") params.scope = "mine";
    if (typeF !== ALL) params.record_type = typeF;
    if (statusF !== ALL) params.status = statusF;
    api.get("/records", { params }).then((r) => setRecords(r.data));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [typeF, statusF]);

  const complete = async (r) => {
    await api.put(`/records/${r.id}`, { status: r.status === "completed" ? "scheduled" : "completed" });
    load();
  };
  const del = async (id) => { await api.delete(`/records/${id}`); toast.success("Record deleted"); load(); };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Reviews & Supervisions</h1>
          <p className="mt-1 text-sm text-slate-500">Scheduled supervisions, reviews, assessments and field visits.</p>
        </div>
        <Button data-testid="add-record-button" onClick={() => setOpen(true)} className="bg-orange-500 hover:bg-orange-600">
          <Plus className="mr-1.5 h-4 w-4" /> Schedule
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        <Select value={typeF} onValueChange={setTypeF}>
          <SelectTrigger data-testid="record-type-filter" className="w-56"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All types</SelectItem>
            {RECORD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger data-testid="record-status-filter" className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            <SelectItem value="scheduled">Scheduled</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
        <table data-testid="records-table" className="w-full text-sm">
          <thead className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Type</th>
              <th className="px-4 py-3">Employee</th>
              <th className="px-4 py-3">Scheduled</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {records.map((r) => (
              <tr key={r.id} data-testid="record-row" className="border-b border-slate-100 hover:bg-slate-50">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 font-medium text-slate-900">
                    <CalendarClock className="h-4 w-4 text-orange-500" /> {LABEL[r.record_type] || r.record_type}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => navigate(`/people/${r.employee_id}`)} className="text-slate-700 hover:text-orange-600 hover:underline">{r.employee_name}</button>
                </td>
                <td className="px-4 py-3 text-slate-600">{shortDate(r.scheduled_date)}</td>
                <td className="px-4 py-3">
                  {r.status === "completed" ? (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Completed</span>
                  ) : r.overdue ? (
                    <span className="flex w-fit items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700"><AlertTriangle className="h-3 w-3" /> Overdue</span>
                  ) : (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">Scheduled</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <Button data-testid="complete-record-button" size="sm" variant="outline" onClick={() => complete(r)}>
                      <Check className="mr-1 h-3.5 w-3.5" /> {r.status === "completed" ? "Reopen" : "Complete"}
                    </Button>
                    <button onClick={() => del(r.id)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {records.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-400">No records match your filters.</td></tr>}
          </tbody>
        </table>
      </div>

      <RecordDialog open={open} onOpenChange={setOpen} onSaved={load} />
    </div>
  );
}

export function RecordDialog({ open, onOpenChange, onSaved, presetEmployeeId }) {
  const [employees, setEmployees] = useState([]);
  const [form, setForm] = useState({ employee_id: "", record_type: "supervision", scheduled_date: "", notes: "" });

  useEffect(() => {
    if (!open) return;
    api.get("/employees").then((r) => setEmployees(r.data));
    setForm({ employee_id: presetEmployeeId || "", record_type: "supervision", scheduled_date: "", notes: "" });
  }, [open, presetEmployeeId]);

  const save = async () => {
    if (!form.employee_id) { toast.error("Select an employee"); return; }
    await api.post("/records", { ...form, scheduled_date: form.scheduled_date || null });
    toast.success("Scheduled");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="record-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Schedule a review</DialogTitle>
          <DialogDescription>Create a supervision, review, assessment or field visit.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {!presetEmployeeId && (
            <div>
              <Label className="text-xs uppercase text-slate-500">Employee</Label>
              <Select value={form.employee_id} onValueChange={(v) => setForm((f) => ({ ...f, employee_id: v }))}>
                <SelectTrigger className="mt-1.5" data-testid="record-employee-select"><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div>
            <Label className="text-xs uppercase text-slate-500">Type</Label>
            <Select value={form.record_type} onValueChange={(v) => setForm((f) => ({ ...f, record_type: v }))}>
              <SelectTrigger className="mt-1.5" data-testid="record-type-select"><SelectValue /></SelectTrigger>
              <SelectContent>{RECORD_TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Scheduled date</Label>
            <Input className="mt-1.5" type="date" data-testid="record-date-input" value={form.scheduled_date} onChange={(e) => setForm((f) => ({ ...f, scheduled_date: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Notes</Label>
            <Textarea className="mt-1.5" value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="record-save-button" onClick={save} className="bg-orange-500 hover:bg-orange-600">Schedule</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
